/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef, useState } from 'react'
import cn from 'classnames'
import { connect, useDispatch, useSelector } from 'react-redux'
import { withRouter } from 'react-router-dom'
import { isEmpty } from 'lodash'

import ScanQRGif from '../assets/gifs/scan-qr.gif'

import GratuityHeader from '../components/GratuityHeader'
import SegmentedControlV2 from '../components/segmentedControl/SegmentedControl'
import VNLoader from '../VNLoader/components/VNLoader'
import useCreateOrderInProgress from '../hooks/useCreateOrderInProgress'
import useGetGiftCardBalance from '../hooks/useGetGiftCardBalance'
import { getOrder, getOrderInProgress } from '../selectors/order'
import { centsToDollars } from '../utils/formatters'
import PeripheralBridge from '../utils/peripheralBridge'
import { KEY_CODES } from '../kiosk/utils/constants'
import {
  addPaymentToOrderInProgress,
  clearOrderInProgress,
  clearTipFromOrder,
  clearTipOrderInProgress,
  closeNoOpTender,
} from '../actions/order'
import styles from './ScanScreen.module.scss'
import { makeSegmentChoices, onMountScanScreen, registerScanListeners, toastError } from '../utils/scanScreen'
import VNConcessionsNavBar from '../components/VNConcessionsNavBar'
import { handleBalanceLookup, handleFullPpiFlow } from '../VNPPI/Utils'
import { VNPPI_TENDERS, VNPPI_TENDERS_KEY } from '../VNPPI/Enums'
import { MobileScanner } from '../VNMobileScanner/components/MobileScanner'
import { CameraAlt } from '@material-ui/icons'
import { getCameraPermissions, getVNScannerInitialized, getVNScannerResults } from '../VNMobileScanner/Selectors'
import ConfirmModalV2 from '../components/ConfirmModalV2'
import { getDeviceMode } from '../VNMode/Selectors'
import { MODES } from '../VNMode/Reducer'
import { sendCFDScreenNav } from '../VNAndroidSDK/bridgeCalls/VNWebSDKDataSend'
import { CFD_SCREEN } from '../VNCustomerFacingDisplay/Reducer'
import { VNInjectablePpi } from '../VNPPI/containers/VNInjectablePpi'
import { codeAlreadyUsed } from '../VNUtil/VNUtils'
import { isTabbed } from '../utils/orderStates'
import { ToastManager } from '../utils/toastManager'
import { setCurrentPaymentFlow } from '../VNCustomerFacingDisplay/ActionCreators'
import { CFD_POS_PAYMENT_FLOWS } from '../VNCustomerFacingDisplay/Enums'
import useCloseFreeOrder from '../hooks/useCloseFreeOrder'
import { APPLICATION_STRINGS } from '../strings'
import { setBalanceLookupDialogOptions, setMobileScanPaymentDialog } from '../VNDialogs/ActionCreators'
import { floatToDollars } from '../VNUtil/Money'
import { PAYMENT_TYPES } from '../utils/paymentTypes'
import { useAuths } from '../VNTenders/hooks/useAuths'
import { getIsPreAuthTab } from '../utils/orderUtils'
import useSpinner from '../context/hooks/useSpinner'
import { useSyncOrders } from '../VNOrders/hooks/useSync'
import { getUseTaxClasses } from '../selectors/menus'
import { getIsOrderTaxExempt } from '../VNTaxes/Selectors'

const segmentChoices = makeSegmentChoices('Gift Card')

const GiftCard = ({
  className,
  onGoBack,
  needCreateOrder,
  fromBalanceLookup,
  order,
  addPaymentToOrderInProgress,
  balanceDueInCents,
  placeFreeOrder,
  push,
  tabOrderInState,
}) => {
  const classNames = cn(styles.scanScreen, className)
  const isMobile = window.matchMedia('(max-width: 700px)')?.matches
  const [selectedSubmissionType, setSelectedSubmissionType] = useState(isMobile ? 'entry' : 'scan')
  const [isProcessing, setIsProcessing] = useState(false)
  const [giftCardCode, setGiftCardCode] = useState('')
  const [createOrder, orderCreated] = useCreateOrderInProgress(null, fromBalanceLookup)
  const [getGiftCardBalance, { giftCardBalance, dataSucceeded, dataFailed, cardNotRecognized }] = useGetGiftCardBalance()
  const [keyboardIsActive, setKeyboardIsActive] = useState(false)
  const [scanError, setScanError] = useState(false)
  const [mobileScannerEnabled, setMobileScannerEnabled] = useState(true)
  const [scannerLive, setScannerLive] = useState(false)
  const [ppiInitialized, setPpiInitialized] = useState(false)
  const hasNoBalance = +giftCardBalance === 0
  const isMobileScannerInitialized = useSelector((state) => getVNScannerInitialized(state))
  const isCameraPermissionAllowed = useSelector((state) => getCameraPermissions(state))
  const mobileScanResults = useSelector((state) => getVNScannerResults(state))
  const cfdMode = useSelector((state) => getDeviceMode(state))
  const isTabOrder = isTabbed(order)
  const dispatch = useDispatch()
  const { closeFreeOrder } = useCloseFreeOrder()
  const { stashAuthPaymentForLaterUse } = useAuths()
  const { syncOrderWithAuthIfNeeded } = useSyncOrders()
  const { startSpinner, stopSpinner } = useSpinner()
  const orderStillHasRemainingBalance = useMemo(() => {
    return (balanceDueInCents && balanceDueInCents !== order.amountInCents) || order?.ppiDiscountUsed
  }, [balanceDueInCents, order])

  const useTaxClasses = useSelector(getUseTaxClasses)

  const taxExempt = useSelector(getIsOrderTaxExempt)

  const GiftCardRef = useRef()

  const ppiInjectableRef = useRef()

  /**
   * This should be only called once, calling it more than one time causes lag.
   * Android has fixed this in APK 2.16.0 but legacy versions still have this issue so be careful.
   */
  const stopScanner = () => {
    const bridgeInstance = PeripheralBridge.getBridge()

    if (!bridgeInstance) return
    if (!scannerLive) return

    setScannerLive(false)
    bridgeInstance.call('stopScanner')
  }

  const closeKeyBoard = () => {
    if (keyboardIsActive) {
      GiftCardRef?.current?.blur()
    }
  }

  const handleFocusChange = () => {
    setTimeout(() => {
      setKeyboardIsActive(document.activeElement === GiftCardRef?.current)
    }, 150)
  }

  // sync offline auth tabs before processing
  useEffect(() => {
    const syncCurrentOrder = async () => {
      if (!getIsPreAuthTab(order)) return false

      // stash payment if order was already created in stadium
      if (order?.wasCreatedInStadium) {
        stashAuthPaymentForLaterUse()
        return
      }

      startSpinner()
      try {
        await syncOrderWithAuthIfNeeded(true, false)
      } catch (error) {
        console.error('Error syncing order:', error)
      } finally {
        stopSpinner()
      }
    }
    syncCurrentOrder()
    return stopSpinner
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (needCreateOrder) {
      createOrder()
    }

    return onMountScanScreen(handleFocusChange)
  }, [])

  useEffect(() => {
    if (cfdMode === MODES.POS) {
      sendCFDScreenNav(CFD_SCREEN.GIFT_CARD_PAYMENT)
    }
  }, [])

  useEffect(() => {
    if (dataFailed) {
      setScanError(true)
      setIsProcessing(false)
      if (cardNotRecognized) {
        toastError('Card not recognized. Please try again.', isMobile)
      } else {
        toastError('Error requesting card balance. Please try again.', isMobile)
      }
    }
  }, [dataFailed])

  useEffect(() => {
    if ((orderCreated || isTabOrder) && order?.amountInCents === 0) {
      closeFreeOrder(order.uuid, isTabOrder)
    }
  }, [orderCreated, isTabOrder])

  useEffect(() => {
    if (dataSucceeded) {
      handleSuccess()
    }
  }, [dataSucceeded])

  useEffect(() => {
    if (needCreateOrder || fromBalanceLookup) return
    // current payment flow is needed because the screen is being sent over while the component is unmounting.

    if (balanceDueInCents && orderStillHasRemainingBalance && isMobile) {
      dispatch(setMobileScanPaymentDialog(true, PAYMENT_TYPES.GIFT_CARD, balanceDueInCents, 'gift card'))
      return
    }

    if (cfdMode !== MODES.POS || balanceDueInCents <= 0) {
      return
    }

    if (orderStillHasRemainingBalance) {
      sendCFDScreenNav(CFD_SCREEN.BALANCE_DUE, {
        balanceToBePaid: { amountPaid: order?.amountInCents - balanceDueInCents, balanceDue: balanceDueInCents },
      })
    }
  }, [balanceDueInCents])

  const onScanSuccess = (scannedCode) => {
    if (fromBalanceLookup) {
      checkBalance(scannedCode)
      return
    }

    setGiftCardCode(scannedCode)
    startBalanceCheck(scannedCode)
  }

  useEffect(() => {
    const bridgeInstance = PeripheralBridge.getBridge()

    if (selectedSubmissionType === 'entry' && GiftCardRef.current) {
      GiftCardRef.current?.focus()
      if (!bridgeInstance) return

      stopScanner()
      setScannerLive(false)
    }
  }, [selectedSubmissionType])

  useEffect(() => {
    const bridgeInstance = PeripheralBridge.getBridge()

    if (selectedSubmissionType === 'scan' && (!isEmpty(order) || fromBalanceLookup)) {
      if (!bridgeInstance) return

      setScannerLive(true)
      registerScanListeners({
        order,
        onSuccess: onScanSuccess,
        paymentType: 'Gift card',
        setSelectedSubmissionType,
        isMobile,
        setScanError,
      })
    }
  }, [selectedSubmissionType, order])

  useEffect(() => {
    const bridgeInstance = PeripheralBridge.getBridge()
    if (!bridgeInstance) return

    if (isProcessing || selectedSubmissionType === 'entry') {
      stopScanner()
      setScannerLive(false)
    } else if (selectedSubmissionType === 'scan' && ppiInitialized) {
      setScannerLive(true)
      bridgeInstance.call('startScanner')
    }
  }, [isProcessing, selectedSubmissionType, ppiInitialized])

  useEffect(() => {
    if (mobileScanResults && mobileScanResults?.get('results')) {
      onMobileScan(mobileScanResults?.get('results'))
    }
  }, [mobileScanResults])

  const primaryText = fromBalanceLookup ? APPLICATION_STRINGS.AVAILABLE_BALANCE_TITLE : centsToDollars(balanceDueInCents)
  const secondaryText = selectedSubmissionType === 'scan' ? 'Please scan gift card' : 'Please type gift card code'
  const extraText =
    !!orderStillHasRemainingBalance &&
    `Total: ${centsToDollars(order?.amountInCents)} • Balance Paid: ${centsToDollars(order?.amountInCents - balanceDueInCents)}`

  const handleSubmissionTypeChange = (value) => {
    if (selectedSubmissionType !== value) {
      setSelectedSubmissionType(value)
    }
  }

  const redirect = (tenderAmountInCents) => {
    if (cfdMode === MODES.POS) {
      sendCFDScreenNav(CFD_SCREEN.BALANCE_DUE, {
        balanceToBePaid: { amountPaid: order?.amountInCents - tenderAmountInCents, balanceDue: tenderAmountInCents },
      })
    }

    push({
      pathname: '/tender-selection',
      state: {
        tenderAmountInCents,
        previousPaymentType: PAYMENT_TYPES.GIFT_CARD,
        fromSplit: true,
      },
    })
  }

  const resetTicket = () => {
    setGiftCardCode('')
    setIsProcessing(false)
  }

  /**
   *
   * @param {String} giftCardCode - The value that was entered for the gift card
   */
  const startBalanceCheck = async (giftCardCode) => {
    setIsProcessing(true)

    const ppiInjectableData = ppiInjectableRef.current.getData(VNPPI_TENDERS.GIFTCARD_TENDER)

    if (!isEmpty(ppiInjectableData.ppiTender)) {
      const externalFunctionality = {
        functions: {
          ...ppiInjectableData.functions,
          setError: setScanError,
          placeFreeOrder: placeFreeOrder,
        },
        balanceDueInCents: ppiInjectableData.order?.balanceDueInCents,
        promotions: ppiInjectableData.promotions,
        token: giftCardCode,
        paymentType: VNPPI_TENDERS_KEY.GIFTCARD_TENDER,
        shortDescription: 'Gift Card',
        useTaxClasses,
        taxExempt: order?.taxExempt ?? taxExempt ?? false,
      }

      await handleFullPpiFlow(ppiInjectableData, externalFunctionality)

      resetTicket()
    } else {
      getGiftCardBalance(giftCardCode)
    }
  }

  // This only gets the balance and does nothing with the balance or order.
  // this is for the balance lookup screen
  const checkBalance = async (ticket) => {
    setIsProcessing(true)
    setScanError(false)

    const ppiInjectableData = ppiInjectableRef.current.getData(VNPPI_TENDERS.GIFTCARD_TENDER)

    try {
      const data = await handleBalanceLookup(ppiInjectableData, ticket)
      const amount = data?.data?.balance

      if (amount === 0) {
        toastError(APPLICATION_STRINGS.AVAILABLE_BALANCE_LOOKUP_GIFT_CARD_FUND_ERROR, isMobile)
        return
      }

      if (cfdMode === MODES.POS) {
        sendCFDScreenNav(CFD_SCREEN.BALANCE_AVAILABLE, { availableBalance: `$${floatToDollars(amount)}` })
      }
      dispatch(
        setBalanceLookupDialogOptions({
          open: true,
          amount: `$${floatToDollars(amount)}`,
          previousScreen: CFD_SCREEN.GIFT_CARD_PAYMENT,
        }),
      )
    } catch (error) {
      toastError(APPLICATION_STRINGS.AVAILABLE_BALANCE_LOOKUP_GIFT_CARD_SCAN_ERROR, isMobile)
    }

    resetTicket()
  }

  const handleSuccess = () => {
    if (hasNoBalance) {
      setScanError(true)
      toastError('Card has no funds. Please try another card or tender.', isMobile)
      setIsProcessing(false)
      return
    }

    let tenderAmountInCents
    if (giftCardBalance >= balanceDueInCents) {
      tenderAmountInCents = balanceDueInCents
    } else {
      tenderAmountInCents = giftCardBalance
    }

    addPaymentToOrderInProgress({
      tenderAmountInCents,
      paymentType: 'gift_card',
      shortDescription: 'Gift Card',
      token: giftCardCode,
    })

    if (navigator?.userAgent?.includes('A800')) {
      dispatch(setMobileScanPaymentDialog(true, PAYMENT_TYPES.GIFT_CARD, balanceDueInCents))
    }

    ToastManager.success('Scan gift card successful')
  }

  const handleKeyPress = (e) => {
    if (e.keyCode === KEY_CODES.ENTER || e.keyCode === '9' || e.key === 'ENTER' || e.key === 'Tab') {
      // eslint-disable-next-line no-useless-escape
      const isValid = /^[a-z|0-9\-]*?$/gi.test(giftCardCode)
      if (!isValid) {
        toastError('The code entered contains invalid characters', isMobile)
        return
      }
      if (codeAlreadyUsed(order, giftCardCode)) {
        toastError('Gift card already entered and applied to order', isMobile)
        return
      }

      closeKeyBoard()

      // Enforce a slight delay to prevent the on screen keyboard from freezing
      // due to device resources being used for network calls and order processing.
      setTimeout(() => {
        if (fromBalanceLookup) {
          checkBalance(giftCardCode)
          return
        }

        startBalanceCheck(giftCardCode)
      }, 300)
    }
  }

  const onMobileCameraClick = () => {
    setMobileScannerEnabled(true)
  }

  const onMobileScan = (scanResult) => {
    if (codeAlreadyUsed(order, scanResult)) {
      ToastManager.error('QR code already scanned and applied to order')
      return
    }

    onScanSuccess(scanResult)
  }

  const handleTicketCodeChange = (e) => {
    setGiftCardCode(e?.target?.value)
  }

  if (!orderCreated && needCreateOrder && !fromBalanceLookup) {
    return <VNLoader />
  }

  const showOtherTenderOptionButton = () => {
    if (fromBalanceLookup || keyboardIsActive || isProcessing) {
      return false
    }

    // bug with discounts make orderStillHasRemainingBalance as true
    if (balanceDueInCents === 0) return false
    if (dataFailed || hasNoBalance || scanError || orderStillHasRemainingBalance) {
      return true
    }

    return false
  }

  const tenderOptionButtonText = scanError ? 'Pay With Other Method' : "I Don't Have Another Gift Card"

  const chooseTenderOptionButton = showOtherTenderOptionButton() && (
    <div
      className={styles.chooseTenderOption}
      onClick={() => {
        push({
          pathname: '/tender-selection',
          state: {
            tenderAmountInCents: balanceDueInCents,
            previousPaymentType: PAYMENT_TYPES.GIFT_CARD,
            fromSplit: true,
          },
        })
      }}
    >
      {tenderOptionButtonText}
    </div>
  )

  const scanErrorModal = () => {
    if (scanError) {
      return (
        <ConfirmModalV2
          onButtonOneClick={() => {
            if (balanceDueInCents > 0) {
              redirect(balanceDueInCents)
            }
          }}
          onButtonTwoClick={() => {
            setScanError(false)
          }}
          headerText={null}
          subtext={'There was an issue when scanning this gift card. Would you like to try again?'}
          isKiosk={false}
          buttonOneText={'OTHER TENDER'}
          buttonTwoText={'TRY AGAIN'}
        />
      )
    }

    return
  }

  const displayVNInjectablePpi = () => {
    return (
      <VNInjectablePpi
        onMount={() => {
          setPpiInitialized(true)
        }}
        ref={ppiInjectableRef}
      />
    )
  }

  const display = () => {
    if (mobileScannerEnabled && isMobileScannerInitialized && isCameraPermissionAllowed) {
      return (
        <div className={classNames}>
          {displayVNInjectablePpi()}
          <MobileScanner
            onKeyboardClick={() => {
              setMobileScannerEnabled(false)
              setTimeout(() => {
                GiftCardRef?.current?.focus()
              }, 150)
            }}
            goBack={() => {
              onGoBack(tabOrderInState, isTabbed(order), fromBalanceLookup)
            }}
            textDisplay={primaryText}
            toolTipText={isProcessing ? 'Processing...' : 'Please scan gift card'}
            pauseBetweenScansMs={3_000}
          />
          {scanErrorModal()}
        </div>
      )
    }

    return (
      <div className={classNames}>
        {displayVNInjectablePpi()}
        <VNConcessionsNavBar
          onClick={() => {
            closeKeyBoard()
            setTimeout(() => {
              onGoBack(tabOrderInState, isTabbed(order), fromBalanceLookup)
            }, 200)
          }}
          rightAccessory={isCameraPermissionAllowed && isMobileScannerInitialized && <CameraAlt onClick={onMobileCameraClick} />}
          textDisplay={isMobile && primaryText}
        />
        <div className={styles.scanScreen__header}>
          <GratuityHeader
            className={cn(styles.gratuityHeader, {
              [styles.fromBalanceLookup]: fromBalanceLookup && !isMobile,
            })}
            primaryText={isMobile ? '' : primaryText}
            secondaryText={secondaryText}
            extraText={extraText}
          />
        </div>
        {!isMobile && (
          <div className={styles.scanScreen__submissionChoices}>
            <SegmentedControlV2
              className={styles.segmentedControl}
              items={segmentChoices}
              selectedValue={selectedSubmissionType}
              onSelect={handleSubmissionTypeChange}
            />
          </div>
        )}
        <div className={styles.scanScreen__body}>
          <div className={styles.choicesContent}>
            <div
              className={cn(styles.option, {
                [styles.isHidden]: selectedSubmissionType === 'entry' || isProcessing,
              })}
            >
              <img src={ScanQRGif} alt={'Scan Gift Card'} className={styles.qrImage} />
            </div>
            <div
              className={cn(styles.option, {
                [styles.isHidden]: selectedSubmissionType === 'scan' || isProcessing,
              })}
            >
              <input
                className={styles.manualEntry}
                ref={GiftCardRef}
                value={giftCardCode}
                onChange={handleTicketCodeChange}
                onKeyDown={handleKeyPress}
              />
            </div>
            <div
              className={cn(styles.processing, {
                [styles.isHidden]: !isProcessing,
              })}
            >
              <div className={styles.loadingSpinner}>
                <VNLoader processing />
              </div>
              <span className={styles.topText}>Processing...</span>
              <span className={styles.bottomText}>please wait.</span>
            </div>
          </div>
          {chooseTenderOptionButton}
        </div>
      </div>
    )
  }

  return display()
}

const mapStateToProps = (state, props) => {
  const fromBalanceLookup = props?.history?.location?.state?.fromBalanceLookup
  const order = getOrderInProgress(state)
  const balanceDueInCents = order?.balanceDueInCents
  const push = props?.history?.push ?? (() => {})

  // order would only exist in order list state if being a tabbed order
  const tabOrderInState = getOrder(state, order.uuid)

  return {
    order,
    fromBalanceLookup,
    needCreateOrder: isEmpty(order),
    balanceDueInCents,
    push,
    tabOrderInState,
  }
}

const mapDispatchToProps = (dispatch, props) => {
  const goBack = props?.history?.goBack ?? (() => {})

  return {
    onGoBack: (tabOrderInState, isOrderTabbed, fromBalanceLookup) => {
      if (fromBalanceLookup) {
        sendCFDScreenNav(CFD_SCREEN.IDLE)
        dispatch(setCurrentPaymentFlow(CFD_POS_PAYMENT_FLOWS.UNSET, true))
        goBack()
        return
      }

      if (isEmpty(tabOrderInState)) {
        dispatch(clearOrderInProgress())
      } else {
        dispatch(clearTipOrderInProgress(tabOrderInState))
        dispatch(clearTipFromOrder(tabOrderInState.uuid))
      }

      if (isOrderTabbed) {
        sendCFDScreenNav(CFD_SCREEN.IDLE)
      } else {
        sendCFDScreenNav(CFD_SCREEN.CART)
      }

      dispatch(setCurrentPaymentFlow(CFD_POS_PAYMENT_FLOWS.UNSET, true))
      goBack()
    },
    addPaymentToOrderInProgress: (payment) => dispatch(addPaymentToOrderInProgress(payment)),
    placeFreeOrder: (payload) => {
      dispatch(closeNoOpTender(payload))
    },
  }
}
export default withRouter(connect(mapStateToProps, mapDispatchToProps)(GiftCard))
